import { Fragment } from "react";
import type { LinkPreview } from "../../types/chat";
import { Skeleton } from "../../components/ui";

// The only thing a mention check needs from a member.
type MentionMember = { username?: string | null };

export function renderMentions(text: string, keyPrefix: string, members: MentionMember[]) {
  const parts = text.split(/(@all\b|@[A-Za-z0-9_.-]+\b)/gi);
  return parts.map((segment, index) => {
    const token = segment.startsWith('@') ? segment.slice(1).toLowerCase() : '';
    const isValidMention = token === 'all' || members.some((member) =>
      String(member.username || '').trim().toLowerCase() === token
    );
    if (isValidMention) {
      return (
        <span key={`${keyPrefix}-mention-${index}`} className="rounded bg-cyan-400/10 px-1 text-cyan-200">
          {segment}
        </span>
      );
    }
    return <Fragment key={`${keyPrefix}-text-${index}`}>{segment}</Fragment>;
  });
}

// Render *wrapped* segments in italics and visually distinguish mentions.
export function renderWithItalics(text: string, members: MentionMember[]) {
  const parts = text.split(/(\*[^*]+\*)/g);
  return parts.map((seg, i) => {
    if (seg.startsWith('*') && seg.endsWith('*') && seg.length >= 2) {
      return <em key={i} className="italic">{seg.slice(1, -1)}</em>;
    }
    return <Fragment key={i}>{renderMentions(seg, `plain-${i}`, members)}</Fragment>;
  });
}
const URL_REGEX = /(https?:\/\/[^\s]+)/gi;
export function linkifyMessage(text: string, keyPrefix: string, members: MentionMember[]) {
  const nodes: React.ReactNode[] = [];
  const urls: string[] = [];
  if (!text) return { nodes: [text], urls: [] };
  let lastIndex = 0;
  let segKey = 0;
  const regex = new RegExp(URL_REGEX.source, 'gi');
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      const plain = text.slice(lastIndex, match.index);
      nodes.push(
        ...renderWithItalics(plain, members).map((node, idx) => (
          <Fragment key={`${keyPrefix}-seg-${segKey}-${idx}`}>{node}</Fragment>
        ))
      );
      segKey++;
    }
    const url = match[0];
    urls.push(url);
    nodes.push(
      <a
        key={`${keyPrefix}-url-${segKey}`}
        href={url}
        target="_blank"
        rel="noreferrer"
        className="text-emerald-300 hover:text-emerald-100 break-all underline-offset-2"
      >
        {url}
      </a>
    );
    segKey++;
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    const tail = text.slice(lastIndex);
    nodes.push(
      ...renderWithItalics(tail, members).map((node, idx) => (
        <Fragment key={`${keyPrefix}-seg-${segKey}-${idx}`}>{node}</Fragment>
      ))
    );
  }
  return { nodes, urls: Array.from(new Set(urls)) };
}
export function getTikTokEmbedUrl(raw?: string) {
  if (!raw) return null;
  try {
    const normalized = raw.startsWith('http') ? raw : `https://${raw}`;
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (!host.includes('tiktok.com')) return null;
    const match = parsed.pathname.match(/\/(video|photo)\/(\d+)/);
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    const fallbackId = pathParts[pathParts.length - 1];
    const id = match?.[2] || fallbackId;
    return id ? `https://www.tiktok.com/embed/v2/${id}` : null;
  } catch {
    return null;
  }
}
export function getInstagramEmbedUrl(raw?: string) {
  if (!raw) return null;
  try {
    const normalized = raw.startsWith('http') ? raw : `https://${raw}`;
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();
    if (!host.includes('instagram.com')) return null;
    const match = parsed.pathname.match(/\/(reel|p|tv|stories)\/([^/?#]+)/);
    if (!match) return null;
    const id = match[2]?.replace(/\/+$/, '');
    if (!id) return null;
    return `https://www.instagram.com/${match[1]}/${id}/embed`;
  } catch {
    return null;
  }
}
export function renderLinkPreview(preview: LinkPreview, key: string) {
  if (!preview?.url) return null;
  const href = preview.url.startsWith('http') ? preview.url : `https://${preview.url}`;
  const host = (() => {
    try {
      return new URL(preview.url).hostname.replace(/^www\./, '');
    } catch {
      return preview.siteName || '';
    }
  })();
  const baseCard = "rounded-2xl border border-white/8 bg-neutral-950/80 backdrop-blur shadow-[0_10px_40px_rgba(0,0,0,0.45)] overflow-hidden";
  const accentBar = <div className="h-[2px] bg-emerald-400/60" />;
  const hostLabel = host || preview.siteName || 'Link';
  const tikTokEmbed = getTikTokEmbedUrl(preview.url);
  const instagramEmbed = getInstagramEmbedUrl(preview.url);
  const actionLink = (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-meta text-emerald-300 hover:text-emerald-100">
      <span>Open</span>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-[11px] w-[11px]">
        <path d="M7 17 17 7" />
        <path d="m8 7 9 0 0 9" />
      </svg>
    </a>
  );
  const isVideo =
    (preview.mediaType && preview.mediaType.toLowerCase().startsWith('video/')) ||
    /\.(mp4|webm|mov)$/i.test(preview.url || '');
  const mediaBlock = isVideo ? (
    <video
      src={href}
      poster={preview.image}
      className="h-full w-full object-cover"
      muted
      playsInline
      autoPlay
      loop
      controls={false}
    />
  ) : preview.image ? (
    <img
      src={preview.image}
      alt={preview.title || hostLabel || 'preview'}
      className="h-full w-full object-cover"
      loading="lazy"
    />
  ) : (
    <div className="absolute inset-0 flex items-center justify-center text-meta text-fg-3">
      No image
    </div>
  );
  const isSkeleton = !preview.title && !preview.description && !preview.image && !preview.siteName;

  if (tikTokEmbed) {
    const media = (
      <div
        className="relative aspect-[9/16] w-full max-w-[320px] overflow-hidden border-t border-white/10 bg-neutral-950/80 mx-auto"
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
      >
        <iframe
          title={preview.title || 'TikTok'}
          src={`${tikTokEmbed}?lang=en`}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          loading="lazy"
          scrolling="no"
        />
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.65)]" />
      </div>
    );
    return (
      <div key={key} className={`${baseCard} space-y-0 w-full max-w-[320px] mx-auto`}>
        {accentBar}
        {media}
      </div>
    );
  }

  if (instagramEmbed) {
    const media = (
      <div
        className="relative aspect-[9/16] w-full max-w-[320px] overflow-hidden border-t border-white/10 bg-neutral-950/80 mx-auto"
        onWheel={(e) => e.preventDefault()}
        onTouchMove={(e) => e.preventDefault()}
      >
        <iframe
          title={preview.title || 'Instagram'}
          src={`${instagramEmbed}?embed_source=echo`}
          className="absolute inset-0 h-[120%] w-[120%] -top-[10%] -left-[10%] scale-[1.08] origin-center"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
          loading="lazy"
          scrolling="no"
        />
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_60px_rgba(0,0,0,0.65)]" />
      </div>
    );
    return (
      <div key={key} className={`${baseCard} space-y-0 w-full max-w-[320px] mx-auto`}>
        {accentBar}
        {media}
      </div>
    );
  }
  if (isSkeleton) {
    return (
      <div key={key} className={`${baseCard} p-3 space-y-3 w-full max-w-[320px] mx-auto`}>
        {accentBar}
        <div className="grid grid-cols-[80px_1fr] gap-3 items-center">
          <Skeleton className="aspect-[4/3] max-h-[160px] rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-3 w-24 rounded-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`${baseCard} group block w-full max-w-[320px] mx-auto hover:border-emerald-400/50 hover:shadow-[0_14px_46px_rgba(0,0,0,0.55)] transition`}
    >
      {accentBar}
      <div className="p-3 sm:p-4">
        <div className="grid grid-cols-[80px_1fr] gap-3 items-center">
          <div className="relative overflow-hidden rounded-xl border border-white/5 bg-neutral-900/60 aspect-[4/3] max-h-[160px]">
            {mediaBlock}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/25" />
          </div>
          <div className="space-y-2 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/5 px-2 py-1 text-meta text-fg-2">
                {hostLabel}
              </span>
              {actionLink}
            </div>
            <div className="text-sm font-medium text-white line-clamp-2">{preview.title || preview.url}</div>
            {preview.description && <div className="text-xs text-fg-2 line-clamp-2">{preview.description}</div>}
          </div>
        </div>
      </div>
    </a>
  );
}
